/**
 * Authentication and session.
 *
 * Replaces ~600 lines spread across app.js (`handleDoctorLogin`,
 * `handlePatientAuth`, `loginPatient`, `loginTeamMember`, `loginAs`, `logout`
 * and the four session-restore blocks inside `window.onload`), all of which did
 * the same three things: call the API, stash a session, then hand-toggle CSS
 * classes on `.role-*` elements. Routing replaces the class toggling entirely.
 *
 * Session shape is kept compatible with the legacy `capsule_session` entry so a
 * user who was mid-session during the upgrade is not logged out.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi, doctorApi } from '../api/endpoints';
import { tokenStore, actingInstitution, UNAUTHORIZED_EVENT } from '../api/client';
import { normalizeDoctor } from '../domain/catalog';

const SESSION_KEY = 'capsule_session';

export const ROLES = {
  ADMIN: 'admin',
  DOCTOR: 'doctor',
  PATIENT: 'patient',
  TEAM_MEMBER: 'team_member',
  // A hospital, polyclinic or lab centre manager. Not a doctor: they own
  // doctors, sell services that are not consultations, and take the money.
  INSTITUTION: 'institution',

  /**
   * The four hospital staff logins.
   *
   * There is no role here for the hospital's *doctor*: they hold ROLES.DOCTOR
   * like any other, so they stay bookable through the patient catalogue and
   * keep the doctor screens. What restricts them is a capability they lack
   * (PERSONAL_CLINIC_MANAGE), which is what the upsell modal renders from.
   */
  CO_ADMIN: 'co_admin',
  RECEPTIONIST: 'receptionist',
  OPERATIONS_MANAGER: 'operations_manager',
  SECRETARY: 'secretary',

  /** The seven department desks of the 14-dashboard upgrade. */
  CONTRACTS_MANAGER: 'contracts_manager',
  ACCOUNTS_MANAGER: 'accounts_manager',
  ROOMS_MANAGER: 'rooms_manager',
  NURSE: 'nurse',
  LAB_TECHNICIAN: 'lab_technician',
  RADIOLOGIST: 'radiologist',
  PHARMACIST: 'pharmacist',
};

/**
 * Where each role lands after signing in.
 *
 * A role missing from this map is not a small bug: LandingRedirect reads it with
 * `|| '/login'` (guards.jsx), so a *successful* sign-in bounces straight back to
 * the login screen and reads as broken authentication rather than as a missing
 * line here.
 */
export const ROLE_HOME = {
  [ROLES.ADMIN]: '/admin',
  [ROLES.DOCTOR]: '/doctor',
  [ROLES.PATIENT]: '/patient',
  [ROLES.TEAM_MEMBER]: '/team',
  [ROLES.INSTITUTION]: '/institution',

  [ROLES.CO_ADMIN]: '/hospital',
  [ROLES.RECEPTIONIST]: '/reception',
  [ROLES.OPERATIONS_MANAGER]: '/operations',
  [ROLES.SECRETARY]: '/secretary',

  [ROLES.CONTRACTS_MANAGER]: '/contracts',
  [ROLES.ACCOUNTS_MANAGER]: '/accounts',
  [ROLES.ROOMS_MANAGER]: '/rooms',
  [ROLES.NURSE]: '/nursing',
  [ROLES.LAB_TECHNICIAN]: '/lab',
  [ROLES.RADIOLOGIST]: '/radiology',
  [ROLES.PHARMACIST]: '/pharmacy',
};

/** The hospital staff roles, for the handful of places that branch on "is staff". */
export const HOSPITAL_STAFF_ROLES = [
  ROLES.CO_ADMIN,
  ROLES.RECEPTIONIST,
  ROLES.OPERATIONS_MANAGER,
  ROLES.SECRETARY,
  ROLES.CONTRACTS_MANAGER,
  ROLES.ACCOUNTS_MANAGER,
  ROLES.ROOMS_MANAGER,
  ROLES.NURSE,
  ROLES.LAB_TECHNICIAN,
  ROLES.RADIOLOGIST,
  ROLES.PHARMACIST,
];

const AuthContext = createContext(null);

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.role) return null;

    // Legacy sessions prefixed team-member ids with "TM_". Strip it so the id is
    // numeric, which is what every endpoint expects.
    if (typeof session.patientId === 'string' && session.patientId.startsWith('TM_')) {
      session.patientId = parseInt(session.patientId.replace('TM_', ''), 10);
    }
    return session;
  } catch {
    return null;
  }
}

function writeSession(session) {
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* storage unavailable — the session simply will not survive a reload */
  }
}

/** Turn an API `user` object into the session record the app stores. */
function sessionFromUser(user) {
  const base = {
    role: user.role,
    id: user.id,
    name: user.name,
    username: user.username,
    phone: user.phone ?? null,
    picture: user.picture ?? null,
  };

  switch (user.role) {
    case ROLES.PATIENT:
      return { ...base, patientId: user.id };
    case ROLES.DOCTOR:
      return { ...base, doctorId: user.id };
    case ROLES.TEAM_MEMBER:
      // Team members also get patient-side features, so they carry both ids.
      return { ...base, teamMemberId: user.id, patientId: user.id };
    case ROLES.ADMIN:
      // An admin is a person who also sees doctors. Without a patientId the
      // patient screens have nothing to filter on and render empty, so the
      // admin account doubles as its own patient record — the same way a team
      // member’s does. It scopes to their own id, so this grants no reach into
      // anyone else’s history.
      return { ...base, adminId: user.id, patientId: user.id };
    case ROLES.INSTITUTION:
      // The manager of a hospital or centre. The institution itself is
      // resolved server-side from the token on every request, so nothing about
      // which institution they own is trusted from here.
      return { ...base, institutionManagerId: user.id };
    case ROLES.CO_ADMIN:
    case ROLES.RECEPTIONIST:
    case ROLES.OPERATIONS_MANAGER:
    case ROLES.SECRETARY:
    case ROLES.CONTRACTS_MANAGER:
    case ROLES.ACCOUNTS_MANAGER:
    case ROLES.ROOMS_MANAGER:
    case ROLES.NURSE:
    case ROLES.LAB_TECHNICIAN:
    case ROLES.RADIOLOGIST:
    case ROLES.PHARMACIST:
      // Hospital staff carry a staffId and nothing else. Which hospital they
      // work for, and which doctor a secretary serves, are deliberately absent:
      // both come from GET /api/auth/capabilities, which resolves them from the
      // membership row. Keeping them out of the session means a tampered
      // sessionStorage cannot widen anybody's reach — and the server would
      // ignore it regardless, because it never reads either from the request.
      return { ...base, staffId: user.id };
    default:
      return base;
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readSession());
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [error, setError] = useState(null);

  const isAuthenticated = Boolean(session && tokenStore.get());

  /* ---------------------------------------------------------------------- */
  /* A 401 anywhere in the app tears the session down here, in one place.    */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    const onUnauthorized = (event) => {
      console.warn(
        `[Auth] Session rejected by the API (401 on ${event.detail?.endpoint}). Signing out.`,
      );
      writeSession(null);
      setSession(null);
      setDoctorProfile(null);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* A doctor session needs the full profile (prices, clinics, branding).     */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;
    if (session?.role !== ROLES.DOCTOR || !tokenStore.get()) {
      setDoctorProfile(null);
      return undefined;
    }

    doctorApi
      .profile()
      .then((profile) => {
        if (!cancelled) setDoctorProfile(normalizeDoctor(profile));
      })
      .catch((err) => {
        // Not fatal: the dashboard degrades to the session's basic fields.
        console.error('[Auth] Could not load the doctor profile:', err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [session?.role, session?.id]);

  const applyLogin = useCallback((token, user) => {
    tokenStore.set(token);
    const next = sessionFromUser(user);
    writeSession(next);
    setSession(next);
    setError(null);
    return next;
  }, []);

  /**
   * Username/password sign-in for every role.
   *
   * The backend rejects suspended and pending accounts with a 403 and its own
   * message; those messages are surfaced verbatim, since the doctor-suspension
   * text ("pay your subscription") is the one users are told to act on.
   */
  const login = useCallback(
    async (username, password) => {
      setStatus('loading');
      setError(null);
      try {
        const result = await authApi.login(username, password);
        if (!result?.token || !result?.user) {
          throw new Error('استجابة غير متوقعة من الخادم');
        }
        const next = applyLogin(result.token, result.user);
        setStatus('idle');
        return next;
      } catch (err) {
        setStatus('error');
        setError(err.message);
        throw err;
      }
    },
    [applyLogin],
  );

  /** Self-registration. Doctors come back as `pending` and cannot sign in yet. */
  const register = useCallback(async (payload) => {
    setStatus('loading');
    setError(null);
    try {
      const result = await authApi.register(payload);
      setStatus('idle');
      return result;
    } catch (err) {
      setStatus('error');
      setError(err.message);
      throw err;
    }
  }, []);

  /** Finish a Google sign-in once the backend has verified the credential. */
  const loginWithGoogleResult = useCallback(
    (result) => {
      if (!result?.token || !result?.user) return null;
      return applyLogin(result.token, result.user);
    },
    [applyLogin],
  );

  const logout = useCallback(() => {
    tokenStore.clear();
    // A Super Admin who was working inside a hospital leaves it on sign-out.
    actingInstitution.clear();
    writeSession(null);
    setSession(null);
    setDoctorProfile(null);
    setError(null);
  }, []);

  /** Persist a partial session change, e.g. an updated avatar. */
  const updateSession = useCallback((patch) => {
    setSession((current) => {
      if (!current) return current;
      const next = { ...current, ...patch };
      writeSession(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      session,
      user: session,
      role: session?.role ?? null,
      isAuthenticated,
      doctorProfile,
      setDoctorProfile,
      status,
      error,
      login,
      register,
      loginWithGoogleResult,
      logout,
      updateSession,
      // Convenience ids — these are read constantly across the feature modules.
      doctorId: session?.doctorId ?? (session?.role === ROLES.DOCTOR ? session.id : null),
      patientId: session?.patientId ?? null,
      teamMemberId: session?.teamMemberId ?? null,
      homePath: session ? ROLE_HOME[session.role] || '/' : '/login',
    }),
    [
      session,
      isAuthenticated,
      doctorProfile,
      status,
      error,
      login,
      register,
      loginWithGoogleResult,
      logout,
      updateSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an <AuthProvider>');
  return context;
}

export { SESSION_KEY };
